import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutGrid,
  User,
  Image as ImageIcon,
  Briefcase,
  Inbox,
  Trophy,
  CalendarDays,
  Bell,
  Crown,
  Settings,
  Menu,
  X,
  ChevronRight,
  Share2,
  ExternalLink,
  Eye,
  Sparkles as MissionIcon,
  Compass,
  Users as NetworkIcon,
  MessageCircle,
} from "lucide-react";
import NetworkBadge from "@/components/NetworkBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/talent", label: "Tableau de bord", icon: LayoutGrid, exact: true },
  // Profil regroupe : Mon profil + Portfolio + Univers créatif
  { to: "/talent/profile", label: "Profil", icon: User, group: ["/talent/profile", "/talent/portfolio", "/talent/universe"] },
  // Opportunités regroupe : Collaborations + Candidatures + Missions
  { to: "/collaborations", label: "Opportunités", icon: Briefcase, group: ["/collaborations", "/talent/applications", "/talent/missions"] },
  // Événements regroupe : Événements + Concours + Castings
  { to: "/events", label: "Événements", icon: CalendarDays, group: ["/events", "/talent/concours", "/talent/castings"] },
  { to: "/talent/createurs", label: "Créateurs", icon: Compass },
  { to: "/reseau", label: "Réseau", icon: NetworkIcon, badge: "requests" },
  { to: "/messages", label: "Messages", icon: MessageCircle, badge: "messages" },
  { to: "/talent/premium", label: "Premium", icon: Crown, gold: true },
  { to: "/talent/settings", label: "Paramètres", icon: Settings },
];

export default function DashboardLayout({ children, progress = 0 }) {
  const { user, profile, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.to;
    if (item.group) return item.group.some((g) => location.pathname.startsWith(g));
    return location.pathname.startsWith(item.to);
  };

  const firstName = user?.name?.split(" ")[0] || "Talent";

  const shareUrl = `${window.location.origin}/talents/${user?.user_id}`;

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `${user?.name} • Helencia`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Lien du portfolio copié !");
      }
    } catch {
      // cancelled or blocked
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="dashboard-layout">
      {/* Sidebar desktop — caramel warm */}
      <aside className="hidden lg:flex fixed top-0 left-0 h-screen w-64 flex-col bg-gradient-to-b from-[#C68E59] to-[#A26F3E] text-white z-30">
        <div className="px-6 pt-7 pb-5 border-b border-white/15">
          <Link to="/talent" className="flex items-center gap-2.5 select-none" data-testid="sidebar-logo" aria-label="Helencia">
            <img src="/helencia-logo-256.png" alt="" width={36} height={36} className="rounded-xl" style={{ width: 36, height: 36 }} />
            <span className="font-serif text-[26px] font-medium tracking-tight text-white">
              Helencia
            </span>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-xl text-sm transition-all ${
                  active
                    ? "bg-white/95 text-[#1A1A1A] font-semibold shadow-sm"
                    : "text-white/80 hover:bg-white/15 hover:text-white"
                }`}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className={`w-4 h-4 ${active ? "text-[#C68E59]" : ""}`} />
                <span>{item.label}</span>
                {item.badge && <NetworkBadge kind={item.badge} dark={active} />}
                {active && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 m-3 rounded-2xl bg-[#1A1A1A]/35 border border-white/15 backdrop-blur text-white">
          <Crown className="w-5 h-5 text-[#E6C29A] mb-2" />
          <p className="font-serif text-lg leading-tight mb-1">Passez Premium</p>
          <p className="text-xs text-white/75 leading-relaxed mb-3">
            Photos illimitées & plus de visibilité.
          </p>
          <Link to="/talent/premium">
            <button className="w-full bg-white text-[#A26F3E] text-xs font-semibold tracking-wide rounded-full py-2 hover:bg-[#FAF3EA] transition">
              Découvrir
            </button>
          </Link>
        </div>
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="fixed top-0 left-0 h-screen w-72 bg-gradient-to-b from-[#C68E59] to-[#A26F3E] text-white z-50 lg:hidden shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/15">
              <span className="font-serif text-2xl text-white">Helencia</span>
              <button onClick={() => setMobileOpen(false)} className="p-1 text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="p-3">
              {NAV.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 my-0.5 rounded-xl text-sm ${
                      active ? "bg-white/95 text-[#1A1A1A] font-semibold" : "text-white/85"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </>
      )}

      {/* Main wrapper */}
      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-[#FFFBF5]/85 backdrop-blur-xl border-b border-[#EADDC9]" data-testid="topbar">
          <div className="px-5 sm:px-8 lg:px-10 h-16 flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 -ml-2"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Avatar className="h-9 w-9 border border-[#D4C3A5] flex-shrink-0">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#F5E6D0] text-[#1A1A1A] font-semibold">
                  {user?.name?.[0] || "H"}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block min-w-0">
                <p className="text-xs text-[#8B8680] uppercase tracking-wider">Bonjour</p>
                <p className="text-sm font-semibold text-[#1A1A1A] truncate">{firstName}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/talents/${user?.user_id}`)}
                className="hidden md:inline-flex rounded-full border-[#D4C3A5] text-[#1A1A1A] hover:border-[#1A1A1A] h-9 px-3 text-xs font-semibold tracking-wide"
                data-testid="top-public-profile"
              >
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                Mon profil public
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleShare}
                className="hidden lg:inline-flex rounded-full border-[#D4C3A5] text-[#1A1A1A] hover:border-[#1A1A1A] h-9 px-3 text-xs font-semibold tracking-wide"
                data-testid="top-share-portfolio"
              >
                <Share2 className="w-3.5 h-3.5 mr-1.5" />
                Partager mon portfolio
              </Button>
              <a
                href="/"
                target="_blank"
                rel="noreferrer"
                className="hidden xl:inline-flex items-center h-9 px-3 rounded-full border border-[#D4C3A5] text-[#1A1A1A] hover:border-[#1A1A1A] text-xs font-semibold tracking-wide"
                data-testid="top-view-site"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Voir le site
              </a>
              <Link to="/talent/premium">
                <Button
                  size="sm"
                  className="rounded-full bg-gradient-to-r from-[#D4A574] to-[#C68E59] hover:from-[#C68E59] hover:to-[#A26F3E] text-white h-9 px-4 text-xs font-semibold tracking-wide shadow-[0_4px_14px_-4px_rgba(198,142,89,0.55)]"
                  data-testid="top-premium-btn"
                >
                  <Crown className="w-3.5 h-3.5 mr-1.5" />
                  {profile?.is_premium ? "Premium" : "Passer Premium"}
                </Button>
              </Link>

              <NotificationBell listHref="/talent/notifications" testId="top-notifications-bell" />

              <DropdownMenu>
                <DropdownMenuTrigger className="focus:outline-none">
                  <Avatar className="h-9 w-9 border border-[#D4C3A5] cursor-pointer">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-[#F5E6D0] text-[#1A1A1A] font-semibold">
                      {user?.name?.[0] || "H"}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl">
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold">{user?.name}</p>
                    <p className="text-xs text-[#8B8680]">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/talents/${user?.user_id}`)}>
                    Mon profil public
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleShare}>
                    Partager mon portfolio
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.open("/", "_blank")}>
                    Voir le site public
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/talent/profile")}>
                    Éditer mon profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/talent/settings")}>
                    Paramètres
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>Déconnexion</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="px-5 sm:px-8 lg:px-10 py-8">{children}</main>

        {/* Mobile bottom quick nav — raised above iOS home-indicator via safe-area inset */}
        <div
          className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-[#EADDC9] z-30"
          data-testid="mobile-bottom-nav"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="grid grid-cols-5 h-16">
            {[
              { to: "/talent", icon: LayoutGrid, label: "Accueil" },
              { to: "/collaborations", icon: Briefcase, label: "Collabs" },
              { to: "/talent/portfolio", icon: ImageIcon, label: "Portfolio" },
              { to: "/talent/concours", icon: Trophy, label: "Concours" },
              { to: "/talent/notifications", icon: Bell, label: "Alertes" },
            ].map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex flex-col items-center justify-center gap-0.5 ${
                    active ? "text-[#1A1A1A]" : "text-[#8B8680]"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-[10px] font-semibold tracking-wide">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
        {/* Spacer under content so fixed nav doesn't overlap the last items (incl. iOS safe-area) */}
        <div className="lg:hidden h-16" style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }} />
      </div>
    </div>
  );
}

